"use client";
import { useRef, useEffect, useState } from "react";
import { useTheme } from "next-themes";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

function MapboxMap() {
  const [map, setMap] = useState(null);
  const mapNode = useRef(null);
  const { resolvedTheme } = useTheme();
  const [error, setError] = useState(false);
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);

  // Exact coordinates for Brampton
  const BRAMPTON_CENTER = [-79.64427207723078, 43.589316278706846];
  const INITIAL_ZOOM = 13; // Note the zoom is now 13
  const INITIAL_PITCH = 25;
  const INITIAL_BEARING = 0;

  const add3DBuildings = (mapInstance) => {
    // Only add if the layer doesn't already exist
    if (!mapInstance.getLayer("3d-buildings")) {
      mapInstance.addLayer({
        id: "3d-buildings",
        source: "composite",
        "source-layer": "building",
        filter: ["==", "extrude", "true"],
        type: "fill-extrusion",
        minzoom: 13, // Match this with your initial zoom
        paint: {
          "fill-extrusion-color": [
            "interpolate",
            ["linear"],
            ["get", "height"],
            0,
            "#4a6fa5",
            50,
            "#3b82f6",
            100,
            "#2563eb",
            150,
            "#1d4ed8",
          ],
          "fill-extrusion-height": ["get", "height"],
          "fill-extrusion-base": ["get", "min_height"],
          "fill-extrusion-opacity": 0.8,
          "fill-extrusion-vertical-gradient": true,
        },
      });

      mapInstance.setLight({
        anchor: "viewport",
        color: "#ffffff",
        intensity: 0.4,
        position: [1.5, 180, 45],
      });
    }
  };

  const initializeMap = () => {
    const themeStyle =
      resolvedTheme === "dark"
        ? "mapbox://styles/mapbox/satellite-streets-v12"
        : "mapbox://styles/mapbox/light-v11";

    const mapboxMap = new mapboxgl.Map({
      container: mapNode.current,
      style: themeStyle,
      center: BRAMPTON_CENTER,
      zoom: INITIAL_ZOOM,
      pitch: INITIAL_PITCH,
      interactive: false,
      bearing: INITIAL_BEARING,
      dragPan: false,
      scrollZoom: false,
    });

    mapboxMap.on("error", () => {
      setError(true);
    });

    mapboxMap.on("style.load", () => {
      setStyleLoaded(true);

      // Add 3D buildings immediately after style loads
      if (mapboxMap.getZoom() >= 12) {
        add3DBuildings(mapboxMap);
      }

      // Initial fly-in animation
      setTimeout(() => {
        mapboxMap.flyTo({
          center: BRAMPTON_CENTER,
          zoom: 17,
          pitch: 79,
          bearing: INITIAL_BEARING,
          duration: 8000,
          essential: true,
          easing(t) {
            return t * (2 - t);
          },
        });
      }, 1000);
    });

    // Add click interaction for buildings
    mapboxMap.on("click", "3d-buildings", (e) => {
      if (e.features.length > 0) {
        const coordinates = e.lngLat;
        handleZoomIn(coordinates);
      }
    });

    // Change cursor to pointer when hovering over buildings
    mapboxMap.on("mouseenter", "3d-buildings", () => {
      mapboxMap.getCanvas().style.cursor = "pointer";
    });

    mapboxMap.on("mouseleave", "3d-buildings", () => {
      mapboxMap.getCanvas().style.cursor = "";
    });

    setMap(mapboxMap);
  };

  const handleZoomIn = (coordinates) => {
    if (map) {
      if (isZoomedIn) {
        // Remove building layer before zooming out
        if (map.getLayer("3d-buildings")) {
          map.removeLayer("3d-buildings");
        }

        map.flyTo({
          center: BRAMPTON_CENTER,
          zoom: INITIAL_ZOOM,
          pitch: INITIAL_PITCH,
          bearing: INITIAL_BEARING,
          essential: true,
          duration: 2000,
          easing(t) {
            return t * (2 - t);
          },
        });
        setIsZoomedIn(false);
      } else {
        map.flyTo({
          center: coordinates
            ? [coordinates.lng, coordinates.lat]
            : BRAMPTON_CENTER,
          zoom: 16,
          essential: true,
          duration: 2500,
          pitch: 75,
          bearing: INITIAL_BEARING,
          curve: 1.5,
          speed: 0.8,
          easing(t) {
            return t * (2 - t);
          },
        });
        setIsZoomedIn(true);

        // Add buildings after zoom animation
        setTimeout(() => {
          if (styleLoaded && !map.getLayer("3d-buildings")) {
            add3DBuildings(map);
          }
        }, 1000);
      }
    }
  };

  const rotateMap = () => {
    if (map) {
      map.rotateTo((map.getBearing() - 20) % 360, {
        duration: 10000,
        easing: (t) => t,
      });
    }
  };

  useEffect(() => {
    const node = mapNode.current;
    if (node) {
      node.innerHTML = "";
    }

    mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;

    if (!mapboxgl.accessToken) {
      console.error("Mapbox access token is missing or invalid.");
      setError(true);
      return;
    }

    mapboxgl.supported() ? initializeMap() : setError(true);
  }, [resolvedTheme]);

  useEffect(() => {
    if (map) {
      setTimeout(() => {
        rotateMap();
        map.on("moveend", rotateMap);
      }, 4000); // Start rotation after initial fly-in

      return () => {
        if (map) {
          map.off("moveend", rotateMap);
          map.remove();
        }
      };
    }
  }, [map]);

  if (error) {
    return <div>Error loading the map.</div>;
  }

  return (
    <span className="cursor-grab" onClick={() => handleZoomIn()}>
      <div
        ref={mapNode}
        style={{
          width: "100vw",
          opacity: ".8",
          height: "100%",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 30%, transparent 80%)",
          maskImage: "linear-gradient(to bottom, black 30%, transparent 80%)",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
        }}
      />
    
    </span>
  );
}

export default MapboxMap;
